import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import {
  ClockIcon,
  CloudArrowUpIcon,
  UserPlusIcon,
  EllipsisVerticalIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  TrashIcon,
  ChartBarIcon,
  UsersIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import ApiLoader from "../../ui/ApiLoader";
import Pagination from "../../ui/pagination";
import { useAuth } from "../../../context/AuthContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import BGVForm from "../components/BGVForm";
import AssetsModal from "../modals/AssetsModal";
import {
  disableUserApi,
  enableUserApi,
  refreshMembersByUser,
  terminateUserApi,
  getEndUsers,
  getTeamCompanyUsers,
  syncOdooUsers,
  getDepartments,
  getRolesByDepartment,
  getReportingToEmployees,
  createAdminUser,
  getEmployeeUser,
  updateEmployeeBgv,
  getCompanyByUser,
  exportTeamMembersExcel,
  downloadSampleTeamTemplate,
  bulkUploadTeamMembers,
} from "../../../services/axios/AuthApis/TeamCollabApis";
import UploadTeamMembersModal from "../components/UploadTeamMembersModal";

const getRelativeTime = (isoTimestamp) => {
  if (!isoTimestamp) return "Never";
  const syncDate = new Date(isoTimestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const syncDateOnly = new Date(
    syncDate.getFullYear(),
    syncDate.getMonth(),
    syncDate.getDate(),
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const yesterdayOnly = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate(),
  );
  if (syncDateOnly.getTime() === todayOnly.getTime()) return "Today";
  if (syncDateOnly.getTime() === yesterdayOnly.getTime()) return "Yesterday";
  return syncDate.toLocaleDateString();
};

const isValidProfilePic = (base64) => {
  if (!base64) return false;
  return base64.startsWith("/9j/") || base64.startsWith("iVBOR");
};

const MembersTab = () => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const selectedCompany = useSelector((state) => state.company.selectedCompany);

  // Core state
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [endUser, setEndUser] = useState([]);
  const [teamMembersList, setTeamMembersList] = useState([]);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [syncingOdoo, setSyncingOdoo] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [hrmsSource, setHrmsSource] = useState("none");
  const [customerMasterId, setCustomerMasterId] = useState("");
  const mountedRef = useRef(false);

  // Pagination
  const [pagination, setPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [companyPagination, setCompanyPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
  });

  // Action modal state
  const [actionType, setActionType] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionForm, setActionForm] = useState({
    date: new Date().toISOString().split("T")[0],
    reason: "",
    clearance: false,
  });

  // Add Member modal state
  const [showCreateMemberModal, setShowCreateMemberModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [reportingToRole, setReportingToRole] = useState(null);
  const [reportingToCandidates, setReportingToCandidates] = useState([]);
  const [bgvRequired, setBgvRequired] = useState(false);
  const [doj, setDoj] = useState("");
  const [errors, setErrors] = useState({});
  const [localMember, setLocalMember] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    role: "",
    department: "",
    reporting_to: "",
    bgv_agency: "",
  });
  const hasTeamCollabAccess =
    user?.employee_details?.module_access_to?.["team-collaboration"] === true;
  const [loadingAddMember, setLoadingAddMember] = useState(false);
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  // Fetch customerMasterId for customer_admin and end_user with team collab access
  useEffect(() => {
    if (
      user?.role === "customer_admin" ||
      (user?.role === "end_user" && hasTeamCollabAccess)
    ) {
      const fetchCompanyName = async () => {
        try {
          if (user?.role === "customer_admin") {
            const data = await getCompanyByUser(user.id);
            if (data) setCustomerMasterId(data.customer_master_id || "");
          } else if (user?.role === "end_user" && hasTeamCollabAccess) {
            setCustomerMasterId(user.customer_master_id || "");
          }
        } catch (err) {
          console.error("Error fetching company:", err);
        }
      };
      fetchCompanyName();
    }
  }, [user, hasTeamCollabAccess]);

  // Data fetching
  const fetchEndUsers = useCallback(
    async (page = 1, pageSize = 20) => {
      try {
        setLoading(true);
        setDataReady(false);
        let userId = user?.id;
        if (user?.role === "security_analyst" && selectedCompany) {
          userId = selectedCompany.user_id;
        } else if (user?.role == "end_user" && hasTeamCollabAccess) {
          userId = user?.customer_user_id;
        }
        const data = await getEndUsers(userId, page, pageSize);
        const results = data.results || [];
        setEndUser(results);
        setTeamMembersList(results);
        setPagination({
          totalPages: data.total_pages || 1,
          currentPage: data.current_page || page,
          totalCount: data.count || results.length,
          pageSize: data.page_size || pageSize,
        });
      } catch (err) {
        console.error("Error fetching team members:", err);
      } finally {
        setLoading(false);
        setDataReady(true);
      }
    },
    [user?.id, user?.role, selectedCompany],
  );

  const fetchCompanyEndUsers = useCallback(
    async (page = 1, pageSize = 20) => {
      try {
        setLoading(true);
        setDataReady(false);
        if (!customerMasterId && !selectedCompany) return;
        const customerId = customerMasterId || selectedCompany.id;
        const data = await getTeamCompanyUsers(customerId, page, pageSize);
        setTeamMembersList(data.results || []);
        setEndUser(data.results || []);
        if (data.results?.length > 0 && data.results[0].last_synced_at) {
          setLastSyncedAt(data.results[0].last_synced_at);
        }
        setCompanyPagination({
          totalPages: data.total_pages,
          totalCount: data.count,
          currentPage: data.current_page,
          pageSize: data.page_size,
        });
      } catch (err) {
        console.error("Error fetching company users:", err);
      } finally {
        setLoading(false);
        setDataReady(true);
      }
    },
    [customerMasterId, selectedCompany],
  );

  const handleMembersPageChange = useCallback(
    (page) => {
      if (
        user?.role === "customer_admin" ||
        (user?.role === "security_analyst" && selectedCompany) ||
        (user?.role === "end_user" && hasTeamCollabAccess)
      ) {
        fetchCompanyEndUsers(page, companyPagination.pageSize);
      } else {
        fetchEndUsers(page, pagination.pageSize);
      }
    },
    [
      user?.role,
      selectedCompany,
      fetchCompanyEndUsers,
      fetchEndUsers,
      companyPagination.pageSize,
      pagination.pageSize,
      hasTeamCollabAccess,
    ],
  );

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  useEffect(() => {
    if (!user || !mountedRef.current) return;
    if (user.role === "customer_admin") {
      if (customerMasterId) fetchCompanyEndUsers();
    } else if (user.role === "security_analyst" && selectedCompany) {
      fetchCompanyEndUsers();
    } else if (user.role === "end_user" && hasTeamCollabAccess) {
      if (customerMasterId) fetchCompanyEndUsers();
    } else {
      fetchEndUsers();
    }
  }, [user, customerMasterId, hasTeamCollabAccess]);

  // HRMS sync
  const handleSyncOdooUsers = useCallback(async () => {
    try {
      setSyncingOdoo(true);
      const customerId = selectedCompany?.id || customerMasterId;
      if (!customerId) {
        showSnackbar("No customer selected", "error");
        setSyncingOdoo(false);
        return;
      }
      const response = await syncOdooUsers(customerId, user?.id);
      if (response.status === "error") {
        showSnackbar(
          response.errors?.[0] || "Failed to sync Odoo data",
          "error",
        );
        setSyncingOdoo(false);
        return;
      }
      const syncSummary = [];
      if (response.created_count > 0)
        syncSummary.push(`Created: ${response.created_count}`);
      if (response.updated_count > 0)
        syncSummary.push(`Updated: ${response.updated_count}`);
      if (response.deleted_count > 0)
        syncSummary.push(`Deleted: ${response.deleted_count}`);
      if (response.error_count > 0)
        syncSummary.push(`Errors: ${response.error_count}`);
      if (syncSummary.length > 0) {
        showSnackbar(
          `Sync Complete - ${syncSummary.join(", ")}`,
          response.error_count > 0 ? "warning" : "success",
        );
      }
      if (response.created_users?.[0]?.last_synced_at) {
        setLastSyncedAt(response.created_users[0].last_synced_at);
      }
      if (response.created_users?.length > 0) {
        const normalizedUsers = response.created_users.map((syncedUser) => ({
          id: syncedUser.user_id,
          user_id: syncedUser.user_id,
          employee_id: syncedUser.employee_id,
          name: syncedUser.name,
          firstname: syncedUser.name?.split(" ")[0] || "",
          lastname: syncedUser.name?.split(" ").slice(1).join(" ") || "",
          email: syncedUser.email,
          department: syncedUser.department || "",
          designation: syncedUser.designation || "",
          reporting_to: syncedUser.reporting_to || "",
          action: syncedUser.action || "created",
        }));
        const mergeUsers = (prev) => {
          const updated = [...prev];
          normalizedUsers.forEach((nu) => {
            const idx = updated.findIndex(
              (u) =>
                u.email === nu.email ||
                u.user_id === nu.user_id ||
                u.id === nu.user_id ||
                u.employee_id === nu.employee_id,
            );
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], ...nu };
            } else {
              updated.push(nu);
            }
          });
          return updated;
        };
        setEndUser(mergeUsers);
        setTeamMembersList(mergeUsers);
      }
    } catch (error) {
      console.error("Sync Odoo error:", error);
      showSnackbar(error?.message || "Failed to sync Odoo data", "error");
    } finally {
      setSyncingOdoo(false);
    }
  }, [user?.id, selectedCompany, customerMasterId, showSnackbar]);

  // Action handlers
  const refreshAdminMembers = async () => {
    try {
      const data = await refreshMembersByUser(user.id);
      const list = data.results || data.team_members || [];
      setEndUser(list);
      setTeamMembersList(list);
    } catch (err) {
      console.error("Refresh failed", err);
    }
  };

  const handleTerminateUser = async (userObj, form) => {
    try {
      setLoading(true);
      await terminateUserApi(
        userObj.employee_id,
        {
          date: form.date,
          reason: form.reason,
          clearance_status: form.clearance,
        },
        user.id,
      );
      await refreshAdminMembers();
      showSnackbar("Employee terminated", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDisableUser = async (userObj, form) => {
    try {
      setLoading(true);
      await disableUserApi(
        userObj.employee_id,
        {
          date: form.date,
          reason: form.reason,
          clearance_status: form.clearance,
        },
        user.id,
      );
      await refreshAdminMembers();
      showSnackbar("Employee disabled & auto-check triggered", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEnableUser = async (userObj, form) => {
    try {
      setLoading(true);
      await enableUserApi(
        userObj.employee_id,
        {
          date: form.date,
          reason: form.reason,
          clearance_status: form.clearance,
        },
        user.id,
      );
      await refreshAdminMembers();
      showSnackbar("Employee enabled", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add Member helpers
  const fetchDepartments = async () => {
    try {
      const data = await getDepartments();
      setDepartments(data.results || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchRolesByDepartmentId = async (departmentId) => {
    try {
      const data = await getRolesByDepartment(departmentId);
      setRoles(data.results || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  };

  const fetchReportingToEmployeesForRole = async (roleId) => {
    if (!roleId || !customerMasterId) {
      setReportingToRole(null);
      setReportingToCandidates([]);
      return;
    }
    try {
      const data = await getReportingToEmployees(roleId, customerMasterId);
      if (data.status === "success" && data.data) {
        setReportingToRole({
          role_id: data.data.reporting_to_role_id,
          role_name: data.data.reporting_to_role_name,
        });
        setReportingToCandidates(data.data.employees || []);
        setLocalMember((prev) => {
          if (prev.reporting_to?.trim()) return prev;
          return {
            ...prev,
            reporting_to:
              data.data.employee?.user_name?.trim() ||
              data.data.reporting_to_role_name ||
              "",
          };
        });
      } else {
        setReportingToRole(null);
        setReportingToCandidates([]);
      }
    } catch (err) {
      console.error("Error fetching reporting_to candidates:", err);
      setReportingToRole(null);
      setReportingToCandidates([]);
    }
  };

  useEffect(() => {
    if (showCreateMemberModal) {
      setLocalMember({
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        role: "",
        department: "",
        reporting_to: "",
        bgv_agency: "",
      });
      setDoj("");
      setErrors({});
      setBgvRequired(false);
    }
  }, [showCreateMemberModal]);

  const handleAddMember = async (memberData) => {
    const firstname = memberData?.firstname || "";
    const email = (memberData?.email || "").trim();
    if (!firstname.trim()) {
      setErrors((prev) => ({ ...prev, firstname: "First name is required" }));
      showSnackbar("First name is required", "error");
      return;
    }
    if (!email) {
      setErrors((prev) => ({ ...prev, email: "Email is required" }));
      showSnackbar("Email is required", "error");
      return;
    }
    const payload = {
      firstname: firstname.trim(),
      lastname: memberData?.lastname || "",
      password: "Enduser@123",
      email,
      phone: memberData?.phone || "",
      department: memberData?.department || "",
      designation: memberData?.role || "",
      reporting_to: memberData?.reporting_to || "",
      role: "end_user",
      customer_master_id: customerMasterId,
      date_of_joining: doj || "",
    };
    try {
      setLoadingAddMember(true);
      const data = await createAdminUser(payload);
      const newUserId = data?.data?.id;
      const employeesRes = await getEmployeeUser();
      const employees = employeesRes?.results || [];
      const savedEmployee = employees.find((emp) => emp.user_id === newUserId);
      if (!savedEmployee) {
        showSnackbar("BGV update skipped - employee not found", "error");
        setShowCreateMemberModal(false);
        if (
          user.role === "customer_admin" ||
          (user.role === "security_analyst" && selectedCompany) ||
          (user.role === "end_user" && hasTeamCollabAccess)
        ) {
          fetchCompanyEndUsers();
        } else {
          fetchEndUsers();
        }
        return;
      }
      if (bgvRequired && memberData?.bgv_agency) {
        try {
          await updateEmployeeBgv({
            employeeId: savedEmployee.id,
            agencyId: memberData.bgv_agency,
            userId: user?.id,
          });
          showSnackbar("BGV updated successfully!", "success");
        } catch (err) {
          console.error("Error updating BGV:", err);
          showSnackbar("BGV update failed!", "error");
        }
      }
      showSnackbar("Member added successfully!", "success");
      setShowCreateMemberModal(false);
      if (
        user.role === "customer_admin" ||
        (user.role === "security_analyst" && selectedCompany) ||
        (user.role === "end_user" && hasTeamCollabAccess)
      ) {
        fetchCompanyEndUsers();
      } else {
        fetchEndUsers();
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      showSnackbar(err.message || "Network error.", "error");
    } finally {
      setLoadingAddMember(false);
    }
  };

  const handleSubmit = () => {
    const nextErrors = {};
    if (!localMember.firstname?.trim())
      nextErrors.firstname = "First name is required";
    if (!localMember.email?.trim()) nextErrors.email = "Email is required";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    handleAddMember(localMember);
  };

  const filteredTeamMembers = teamMembersList || [];

  return (
    <div className="space-y-6">
      {loading && endUser.length === 0 && filteredTeamMembers.length === 0 ? (
        <ApiLoader label="Loading team members..." />
      ) : (
        <>
          {(user?.role === "customer_admin" ||
            (user?.role === "security_analyst" && selectedCompany) ||
            (user?.role == "end_user" && hasTeamCollabAccess)) && (
            <div className="flex justify-end items-center space-x-4">
              {lastSyncedAt && (
                <span className="text-sm text-gray-600 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  Last synced:{" "}
                  <span className="font-medium ml-1">
                    {getRelativeTime(lastSyncedAt)}
                  </span>
                </span>
              )}
              {lastSyncedAt && (
                <button
                  onClick={handleSyncOdooUsers}
                  disabled={syncingOdoo}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 border border-transparent rounded-md font-medium text-white hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
                >
                  {syncingOdoo ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                      <span>Sync From HRMS</span>
                    </>
                  )}
                </button>
              )}
              {hrmsSource === "none" && (
                <button
                  onClick={() => {
                    fetchDepartments();
                    setShowCreateMemberModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-medium text-white hover:bg-blue-700"
                >
                  <UserPlusIcon className="w-5 h-5 mr-2" />
                  Add Team Member
                </button>
              )}
              <button
                onClick={async () => {
                  try {
                    const custId = customerMasterId || selectedCompany?.id;
                    if (!custId) {
                      showSnackbar("Customer ID not found", "error");
                      return;
                    }
                    await exportTeamMembersExcel(custId);
                    showSnackbar(
                      "Team members downloaded successfully",
                      "success",
                    );
                  } catch (err) {
                    console.error("Download error:", err);
                    showSnackbar("Failed to download team members", "error");
                  }
                }}
                className="inline-flex items-center px-4 py-2 bg-green-600 border border-transparent rounded-md font-medium text-white hover:bg-green-700"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                Download
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 border border-transparent rounded-md font-medium text-white hover:bg-purple-700"
              >
                <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                Upload
              </button>
            </div>
          )}

          {user?.role === "admin" &&
            (!dataReady || (loading && endUser.length === 0) ? (
              <ApiLoader label="Loading team members..." />
            ) : endUser.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-600">No team members found</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {endUser.map((e) => (
                  <motion.div
                    key={e.employee_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative group rounded-xl shadow-sm border border-gray-200 bg-white hover:shadow-md transition-all duration-300 overflow-hidden"
                  >
                    <div
                      className={`absolute inset-0 bg-white/50 backdrop-blur-sm transition-all duration-300 z-5 ${e?.status_disable === true || e?.status_terminate === true ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                    />
                    <div
                      className={`h-1 transition-colors ${e.status_terminate ? "bg-red-500" : e.status_disable ? "bg-yellow-500" : "bg-green-500"}`}
                    />
                    <div className="relative z-10 p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            {e?.profile_pic ? (
                              <img
                                src={`data:image/png;base64,${e.profile_pic}`}
                                alt={`${e.firstname} ${e.lastname}`}
                                className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <span className="text-gray-600 font-semibold text-xs">
                                  {(
                                    e.company_name?.charAt(0) || "?"
                                  ).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-3 flex-1 min-w-0">
                            <h3
                              className={`text-sm font-bold truncate ${e.status_disable || e.status_terminate ? "text-gray-500" : "text-gray-900"}`}
                            >
                              {e.company_name || "—"}
                            </h3>
                            {e.status_terminate && (
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                                Terminated
                              </span>
                            )}
                            {e.status_disable && !e.status_terminate && (
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-yellow-700 bg-yellow-100 rounded-full">
                                Disabled
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setOpenMenuUserId(
                                openMenuUserId === e.employee_id
                                  ? null
                                  : e.employee_id,
                              );
                            }}
                            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <EllipsisVerticalIcon className="w-4 h-4 text-gray-500" />
                          </button>
                          {openMenuUserId === e.employee_id && (
                            <div className="absolute right-0 top-16 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                              {!e.status_disable ? (
                                <button
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setOpenMenuUserId(null);
                                    setSelectedUser(e);
                                    setActionType("disable");
                                    setActionForm({
                                      date: new Date()
                                        .toISOString()
                                        .split("T")[0],
                                      reason: "",
                                      clearance: false,
                                    });
                                    setShowFormModal(true);
                                  }}
                                  className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-yellow-50 transition-colors"
                                >
                                  <NoSymbolIcon className="w-4 h-4 mr-3 text-yellow-600" />
                                  <span>Disable</span>
                                </button>
                              ) : (
                                <button
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setOpenMenuUserId(null);
                                    setSelectedUser(e);
                                    setActionType("enable");
                                    setActionForm({
                                      date: new Date()
                                        .toISOString()
                                        .split("T")[0],
                                      reason: "",
                                      clearance: false,
                                    });
                                    setShowFormModal(true);
                                  }}
                                  className="w-full flex items-center px-4 py-2.5 text-sm text-green-700 hover:bg-green-50 transition-colors"
                                >
                                  <CheckCircleIcon className="w-4 h-4 mr-3" />
                                  <span>Enable</span>
                                </button>
                              )}
                              <div className="border-t border-gray-100 my-1" />
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setOpenMenuUserId(null);
                                  setSelectedUser(e);
                                  setActionType("terminate");
                                  setActionForm({
                                    date: new Date()
                                      .toISOString()
                                      .split("T")[0],
                                    reason: "",
                                    clearance: false,
                                  });
                                  setShowFormModal(true);
                                }}
                                className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4 mr-3" />
                                <span>Terminate</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="border-t border-gray-100 my-2" />
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <ChartBarIcon className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                              Reports To
                            </p>
                            <p className="text-xs text-gray-700 font-medium truncate">
                              {e.reporting_to ||
                                (e.status_terminate || e.status_disable
                                  ? "—"
                                  : "Not assigned")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <UsersIcon className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                              Department
                            </p>
                            <p className="text-xs text-gray-700 font-medium truncate">
                              {e.department ||
                                (e.status_terminate || e.status_disable
                                  ? "—"
                                  : "Not assigned")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  onPageChange={handleMembersPageChange}
                  loading={loading}
                  className="col-span-full mt-6"
                />
              </div>
            ))}

          {(user?.role === "customer_admin" ||
            (user?.role === "security_analyst" && selectedCompany) ||
            (user?.role == "end_user" && hasTeamCollabAccess)) &&
            (!dataReady || (loading && filteredTeamMembers.length === 0) ? (
              <ApiLoader label="Loading team members..." />
            ) : filteredTeamMembers.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-600">No team members found.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTeamMembers.map((member) => (
                  <motion.div
                    key={member.employee_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative group bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                  >
                    <div
                      className={`absolute inset-0 rounded-lg bg-white/60 backdrop-blur-sm transition-all duration-300 ${member?.status_disable === true || member?.status_terminate === true ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                    />
                    <div className="relative z-10">
                      <div className="flex items-center absolute right-10 top-2">
                        {member.status_terminate && (
                          <span className="text-red-600 text-xs font-bold tracking-wide">
                            TERMINATED
                          </span>
                        )}
                      </div>
                      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-all">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuUserId(
                              openMenuUserId === member.employee_id
                                ? null
                                : member.employee_id,
                            );
                          }}
                          className="p-1 rounded-lg hover:bg-gray-100"
                        >
                          <EllipsisVerticalIcon className="w-4 h-4 text-gray-600" />
                        </button>
                        {openMenuUserId === member.employee_id && (
                          <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 shadow-lg rounded-md z-20">
                            {!member.status_disable ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuUserId(null);
                                  setSelectedUser(member);
                                  setActionType("disable");
                                  setActionForm({
                                    date: new Date()
                                      .toISOString()
                                      .split("T")[0],
                                    reason: "",
                                    clearance: false,
                                  });
                                  setShowFormModal(true);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-gray-700"
                              >
                                <NoSymbolIcon className="w-4 h-4 mr-3 text-red-600" />
                                Disable
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuUserId(null);
                                  setSelectedUser(member);
                                  setActionType("enable");
                                  setActionForm({
                                    date: new Date()
                                      .toISOString()
                                      .split("T")[0],
                                    reason: "",
                                    clearance: false,
                                  });
                                  setShowFormModal(true);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-green-700"
                              >
                                <CheckCircleIcon className="w-4 h-4 mr-3 text-green-600" />
                                Enable
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuUserId(null);
                                setSelectedUser(member);
                                setActionType("terminate");
                                setActionForm({
                                  date: new Date().toISOString().split("T")[0],
                                  reason: "",
                                  clearance: false,
                                });
                                setShowFormModal(true);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <TrashIcon className="w-4 h-4 mr-3" />
                              Terminate
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                        <div className="flex-shrink-0">
                          {isValidProfilePic(member?.profile_pic) ? (
                            <img
                              src={`data:image/png;base64,${member.profile_pic}`}
                              alt={`${member.firstname} ${member.lastname}`}
                              className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                              {(
                                (member?.firstname?.[0] || "") +
                                (member?.lastname?.[0] || "")
                              ).toUpperCase() || "?"}
                            </div>
                            
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-bold text-gray-900">
                            {(
                              (member.firstname || "") +
                              " " +
                              (member.lastname || "")
                            ).trim()}
                          </h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Team Member
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {member.email || "—"}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-xs">
                        <div className="flex items-start gap-2">
                          <ChartBarIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">
                              Reports To
                            </p>
                            <p className="text-gray-700 font-medium truncate">
                              {member.reporting_to || "—"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <UsersIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">
                              Department
                            </p>
                            <p className="text-gray-700 font-medium truncate">
                              {member.department || "—"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* View Assets Button - Only for customer_admin */}
                      {user?.role === "customer_admin" || user?.role === "security_analyst" && (
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUser(member);
                              setShowAssetsModal(true);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-white font-medium text-xs rounded-lg transition-colors"
                          >
                            <CubeIcon className="w-4 h-4" />
                            View Assets
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
                <Pagination
                  currentPage={companyPagination.currentPage}
                  totalPages={companyPagination.totalPages}
                  totalCount={companyPagination.totalCount}
                  pageSize={companyPagination.pageSize}
                  onPageChange={(page, pageSize) =>
                    fetchCompanyEndUsers(page, pageSize)
                  }
                  loading={loading}
                  className="col-span-full mt-6"
                />
              </div>
            ))}

          {(!user ||
            (user?.role == "end_user" && !hasTeamCollabAccess) ||
            user?.role == "auditor") && (
            <div className="text-gray-600">
              No team data available for your role.
            </div>
          )}
        </>
      )}

      {/* Disable / Terminate / Enable modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-96 p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold mb-4">
              {actionType === "disable"
                ? "Disable Employee"
                : actionType === "enable"
                  ? "Enable Employee"
                  : "Terminate Employee"}
            </h2>
            <label className="block text-sm font-medium">Date</label>
            <input
              type="date"
              value={actionForm.date}
              onChange={(e) =>
                setActionForm({ ...actionForm, date: e.target.value })
              }
              className="w-full border px-3 py-2 rounded mb-3"
            />
            <label className="block text-sm font-medium">Reason</label>
            <textarea
              rows="3"
              value={actionForm.reason}
              onChange={(e) =>
                setActionForm({ ...actionForm, reason: e.target.value })
              }
              className="w-full border px-3 py-2 rounded mb-3"
              placeholder="Explain the reason..."
            />
            <label className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                checked={actionForm.clearance}
                onChange={() =>
                  setActionForm({
                    ...actionForm,
                    clearance: !actionForm.clearance,
                  })
                }
              />
              <span className="text-sm">Clearance Completed</span>
            </label>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowFormModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (actionType === "disable")
                    handleDisableUser(selectedUser, actionForm);
                  else if (actionType === "terminate")
                    handleTerminateUser(selectedUser, actionForm);
                  else if (actionType === "enable")
                    handleEnableUser(selectedUser, actionForm);
                  setShowFormModal(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member modal */}
      <AnimatePresence>
        {showCreateMemberModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add Team Member
                  </h3>
                  <button
                    onClick={() => setShowCreateMemberModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={localMember.firstname}
                      onChange={(e) =>
                        setLocalMember((prev) => ({
                          ...prev,
                          firstname: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter first name"
                    />
                    {errors.firstname && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.firstname}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={localMember.lastname || ""}
                      onChange={(e) =>
                        setLocalMember((prev) => ({
                          ...prev,
                          lastname: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter last name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={localMember.email || ""}
                      onChange={(e) => {
                        setLocalMember((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }));
                        setErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      className={`w-full border rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 ${errors.email ? "border-red-500" : "border-gray-300"}`}
                      placeholder="Enter email address"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={localMember.phone || ""}
                      onChange={(e) =>
                        setLocalMember((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter phone number"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <select
                      value={localMember.department || ""}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      onChange={(e) => {
                        const deptId = e.target.value;
                        setSelectedDepartment(deptId);
                        setLocalMember((prev) => ({
                          ...prev,
                          department: deptId,
                        }));
                        if (deptId) {
                          fetchRolesByDepartmentId(deptId);
                        } else {
                          setRoles([]);
                        }
                      }}
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.department_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={localMember.role || ""}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      onChange={(e) => {
                        const selectedRoleId = e.target.value;
                        setLocalMember((prev) => ({
                          ...prev,
                          role: selectedRoleId,
                        }));
                        fetchReportingToEmployeesForRole(selectedRoleId);
                      }}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Joining
                    </label>
                    <input
                      type="date"
                      value={doj}
                      onChange={(e) => setDoj(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reporting To
                    </label>
                    <input
                      type="text"
                      value={localMember.reporting_to || ""}
                      onChange={(e) =>
                        setLocalMember((prev) => ({
                          ...prev,
                          reporting_to: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter reporter name"
                    />
                  </div>

                  <BGVForm
                    isOpen={showCreateMemberModal}
                    bgvRequired={bgvRequired}
                    onBgvRequiredChange={setBgvRequired}
                    localMember={localMember}
                    onLocalMemberChange={setLocalMember}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCreateMemberModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                    disabled={loadingAddMember}
                  >
                    Add Member
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <UploadTeamMembersModal
        show={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        loading={uploadLoading}
        onDownloadTemplate={async () => {
          try {
            await downloadSampleTeamTemplate();
            showSnackbar("Template downloaded successfully", "success");
          } catch (err) {
            showSnackbar("Failed to download template", "error");
          }
        }}
        onUpload={async (file) => {
          try {
            setUploadLoading(true);
            const custId = customerMasterId || selectedCompany?.id;
            if (!custId) {
              showSnackbar("Customer ID not found", "error");
              return;
            }
            const result = await bulkUploadTeamMembers(custId, file);
            showSnackbar(result.message || "Upload successful", "success");
            setShowUploadModal(false);
            // Refresh list
            if (
              user?.role === "customer_admin" ||
              (user?.role === "security_analyst" && selectedCompany) ||
              (user?.role === "end_user" && hasTeamCollabAccess)
            ) {
              fetchCompanyEndUsers();
            } else {
              fetchEndUsers();
            }
          } catch (err) {
            console.error("Upload error:", err);
            showSnackbar(
              err.message || "Failed to upload team members",
              "error",
            );
          } finally {
            setUploadLoading(false);
          }
        }}
      />

      <AssetsModal
        show={showAssetsModal}
        onClose={() => setShowAssetsModal(false)}
        member={selectedUser}
      />
    </div>
  );
};

export default MembersTab;
